import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import axiosInstance from "../../utils/axiosInstance";
import PageWrapper from "../../components/ui/PageWrapper";
import PageHeader from "../../components/ui/PageHeader";
import FlagDetailModal from "../../components/admin/FlagDetailModal";

const TABS = [
  { id: "all", label: "All", type: "" },
  { id: "deviation", label: "Deviations", type: "deviation" },
  { id: "missing_task", label: "Missing tasks", type: "missing_task" },
  { id: "ata", label: "ÄTA", type: "ata" },
];

const TYPE_LABELS = {
  deviation: "Deviation",
  missing_task: "Missing task",
  ata: "ÄTA",
};

const SEVERITY_STYLES = {
  low: "bg-gray-100 text-gray-700",
  medium: "bg-amber-100 text-amber-800",
  blocking: "bg-red-100 text-red-800",
};

const PAGE_SIZE = 25;

const personName = (person) =>
  person ? [person.firstname, person.lastname].filter(Boolean).join(" ") : "—";

/**
 * Central inbox for deviations, missing tasks and ÄTA raised from daily time reports.
 * Scope is derived server side from the admin's memberships, so there is no
 * department picker in the URL.
 */
export default function FollowUpPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get("type") || "all");
  const [status, setStatus] = useState(searchParams.get("status") || "open");
  const [organisationId, setOrganisationId] = useState(searchParams.get("organisationId") || "");
  const [from, setFrom] = useState(searchParams.get("from") || "");
  const [to, setTo] = useState(searchParams.get("to") || "");
  const [query, setQuery] = useState("");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedFlagId, setSelectedFlagId] = useState(null);

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const type = TABS.find((tab) => tab.id === activeTab)?.type || "";
      const resp = await axiosInstance.get("/admin/report-flags", {
        params: {
          ...(type ? { type } : {}),
          ...(status ? { status } : {}),
          ...(organisationId ? { organisationId } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(query.trim() ? { q: query.trim() } : {}),
          page,
          limit: PAGE_SIZE,
        },
      });
      setItems(resp.data.items || []);
      setTotal(resp.data.total || 0);
    } catch (err) {
      console.error("Error fetching report flags", err);
      setError(err.response?.data?.message || "Could not load follow-up items");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab, status, organisationId, from, to, query, page]);

  useEffect(() => {
    const timeout = setTimeout(fetchFlags, query ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [fetchFlags, query]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, status, organisationId, from, to, query]);

  const applyTab = (tabId) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    if (tabId === "all") next.delete("type");
    else next.set("type", TABS.find((tab) => tab.id === tabId).type);
    setSearchParams(next, { replace: true });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const renderTypeBadge = (flag) => (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
      {TYPE_LABELS[flag.type] || flag.type}
    </span>
  );

  const renderStatusBadge = (flag) => (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        flag.status === "resolved"
          ? "bg-green-100 text-green-800"
          : "bg-amber-100 text-amber-800"
      }`}
    >
      {flag.status === "resolved" ? "Resolved" : "Open"}
    </span>
  );

  return (
    <>
      <PageHeader
        title="Follow-up"
        subtitle="Deviations, missing tasks and ÄTA from daily time reports"
        search={{
          value: query,
          onChange: (e) => setQuery(e.target.value),
          placeholder: "Search description...",
        }}
      />
      <PageWrapper>
        <div className="space-y-4">
          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => applyTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-3 py-2 border rounded text-sm"
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="px-3 py-2 border rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="px-3 py-2 border rounded text-sm"
              />
            </div>
            {(from || to || status !== "open" || organisationId) && (
              <button
                type="button"
                onClick={() => {
                  setFrom("");
                  setTo("");
                  setStatus("open");
                  setOrganisationId("");
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Clear filters
              </button>
            )}
            <div className="ml-auto text-sm text-gray-500">
              {loading ? "Loading..." : `${total} item${total === 1 ? "" : "s"}`}
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && items.length === 0 && !error && (
            <div className="py-16 text-center text-gray-500">
              Nothing to follow up on here.
            </div>
          )}

          {/* Desktop table */}
          {items.length > 0 && (
            <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {["Date", "Worker", "Department", "Project", "Type", "Title", "Severity", "Hours", "Status", "Assignee"].map(
                      (heading) => (
                        <th
                          key={heading}
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {heading}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {items.map((flag) => (
                    <tr
                      key={flag._id}
                      onClick={() => setSelectedFlagId(flag._id)}
                      className={`cursor-pointer hover:bg-gray-50 ${flag.isUnseen ? "font-medium" : ""}`}
                    >
                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                        {flag.isUnseen && (
                          <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
                        )}
                        {dayjs(flag.date).format("YYYY-MM-DD")}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                        {personName(flag.createdBy)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">
                        {flag.companyId?.name || "—"}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">{flag.project?.name || "—"}</td>
                      <td className="px-3 py-2">{renderTypeBadge(flag)}</td>
                      <td className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate">
                        {flag.title || flag.proposedTaskName}
                      </td>
                      <td className="px-3 py-2">
                        {flag.type === "deviation" && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                              SEVERITY_STYLES[flag.severity] || SEVERITY_STYLES.medium
                            }`}
                          >
                            {flag.severity}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                        {flag.hoursImpact ?? flag.proposedHours ?? "—"}
                      </td>
                      <td className="px-3 py-2">{renderStatusBadge(flag)}</td>
                      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">
                        {flag.assignedTo ? personName(flag.assignedTo) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile cards */}
          {items.length > 0 && (
            <div className="md:hidden space-y-2">
              {items.map((flag) => (
                <button
                  key={flag._id}
                  type="button"
                  onClick={() => setSelectedFlagId(flag._id)}
                  className="w-full text-left p-3 border border-gray-200 rounded-lg bg-white hover:border-gray-300"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      {flag.isUnseen && <span className="w-2 h-2 rounded-full bg-red-500" />}
                      {renderTypeBadge(flag)}
                    </div>
                    {renderStatusBadge(flag)}
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {flag.title || flag.proposedTaskName}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {dayjs(flag.date).format("YYYY-MM-DD")} · {personName(flag.createdBy)} ·{" "}
                    {flag.project?.name || "—"}
                  </div>
                </button>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 border rounded text-sm disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 border rounded text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </div>

        <FlagDetailModal
          flagId={selectedFlagId}
          isOpen={!!selectedFlagId}
          onClose={() => setSelectedFlagId(null)}
          onChanged={fetchFlags}
        />
      </PageWrapper>
    </>
  );
}
